import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquare, Pin, Lock, Heart, Flag, ArrowLeft, Plus, Send, Clock, User,
} from "lucide-react";

const CATEGORY_LABELS = {
  general: "General", buy_sell: "Buy / Sell",
  recommendations: "Recommendations", events: "Events",
  lost_found: "Lost & Found", announcements: "Announcements",
  off_topic: "Off-Topic",
};

const CATEGORY_TONE = {
  general: "border-white/15 text-zinc-300",
  buy_sell: "border-emerald-500/40 text-emerald-300",
  recommendations: "border-sky-500/40 text-sky-300",
  events: "border-orange-500/40 text-orange-300",
  lost_found: "border-amber-500/40 text-amber-300",
  announcements: "border-fuchsia-500/40 text-fuchsia-300",
  off_topic: "border-zinc-500/40 text-zinc-400",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

// -----------------------------------------------------------------------------
function NewThreadDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/forum/threads", form);
      toast.success("Thread posted");
      setOpen(false);
      setForm({ title: "", body: "", category: "general" });
      onCreated();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="new-thread-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New thread
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle>Start a new thread</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   className="bg-white/[0.04] border-white/10" data-testid="thread-title" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea rows={5} value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                      className="bg-white/[0.04] border-white/10" data-testid="thread-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">Cancel</Button>
          <Button disabled={busy || form.title.length < 3 || !form.body} onClick={submit}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="post-thread-btn">
            {busy ? "Posting…" : "Post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
function ThreadDetail({ threadId, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/forum/threads/${threadId}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  }, [threadId]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/forum/threads/${threadId}/reply`, { body: reply.trim() });
      setReply("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
    finally { setBusy(false); }
  };
  const react = async () => {
    try {
      await api.post(`/forum/threads/${threadId}/react?emoji=like`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const report = async () => {
    const reason = window.prompt("Why are you reporting this thread?");
    if (!reason) return;
    try {
      await api.post(`/forum/threads/${threadId}/report`, { reason });
      toast.success("Thanks — a moderator will review it.");
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const reportPost = async (pid) => {
    const reason = window.prompt("Why are you reporting this post?");
    if (!reason) return;
    try {
      await api.post(`/forum/posts/${pid}/report`, { reason });
      toast.success("Thanks — a moderator will review it.");
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  if (!data) return <div className="text-sm text-zinc-400">Loading…</div>;
  const t = data.thread;
  const likes = (t.reactions?.like || []).length;

  return (
    <div className="space-y-4">
      <Button variant="outline" onClick={onBack} className="border-white/10" data-testid="thread-back">
        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
      </Button>
      <Card className="p-5 border-white/10 bg-card">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {t.pinned && <Badge variant="outline" className="border-fuchsia-500/40 text-fuchsia-300"><Pin className="h-3 w-3 mr-1" />Pinned</Badge>}
              {t.locked && <Badge variant="outline" className="border-red-500/40 text-red-300"><Lock className="h-3 w-3 mr-1" />Locked</Badge>}
              <Badge variant="outline" className={CATEGORY_TONE[t.category] || CATEGORY_TONE.general}>
                {CATEGORY_LABELS[t.category] || t.category}
              </Badge>
            </div>
            <h1 className="text-xl font-semibold text-white font-display">{t.title}</h1>
            <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-1">
              <User className="h-3 w-3" />{t.author_name}
              <Clock className="h-3 w-3 ml-2" />{relTime(t.created_at)}
            </div>
          </div>
        </div>
        <p className="text-sm text-zinc-200 whitespace-pre-wrap mt-2">{t.body}</p>
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">
          <Button size="sm" variant="outline" className="border-white/10 h-7 px-2 text-[11px]"
                  onClick={react} data-testid="thread-react">
            <Heart className="h-3 w-3 mr-1" /> {likes || ""} Like
          </Button>
          <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 h-7 px-2 text-[11px]"
                  onClick={report} data-testid="thread-report">
            <Flag className="h-3 w-3 mr-1" /> Report
          </Button>
        </div>
      </Card>

      <div className="space-y-2">
        {data.posts.map((p) => (
          <Card key={p.id} className="p-4 border-white/10 bg-card" data-testid={`post-${p.id}`}>
            <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-1">
              <User className="h-3 w-3" />{p.author_name}
              <Clock className="h-3 w-3 ml-2" />{relTime(p.created_at)}
              <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">{p.author_role}</Badge>
            </div>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{p.body}</p>
            <div className="mt-2 flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => reportPost(p.id)}
                      className="text-red-300 hover:text-red-200 h-6 px-2 text-[11px]"
                      data-testid={`post-report-${p.id}`}>
                <Flag className="h-3 w-3 mr-1" /> Report
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {!t.locked ? (
        <Card className="p-4 border-white/10 bg-card">
          <Label className="text-xs uppercase tracking-widest text-zinc-500">Reply</Label>
          <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)}
                    placeholder="Share your thoughts…" data-testid="reply-body"
                    className="bg-white/[0.04] border-white/10 mt-2" />
          <div className="mt-2 flex justify-end">
            <Button disabled={busy || !reply.trim()} onClick={send}
                    className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="post-reply-btn">
              <Send className="h-3.5 w-3.5 mr-1" /> {busy ? "Posting…" : "Post reply"}
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-4 border-red-500/20 bg-red-500/[0.04] text-center text-sm text-red-300">
          <Lock className="h-4 w-4 inline mr-2" /> This thread is locked. No new replies accepted.
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
export default function ForumPage() {
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState("all");
  const [openThread, setOpenThread] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = category !== "all" ? { category } : {};
      const { data } = await api.get("/forum/threads", { params });
      setThreads(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  }, [category]);
  useEffect(() => { load(); }, [load]);

  if (openThread) {
    return <ForumPageWrapper>
      <ThreadDetail threadId={openThread} onBack={() => { setOpenThread(null); load(); }} />
    </ForumPageWrapper>;
  }

  return (
    <ForumPageWrapper>
      <PageHeader
        eyebrow="Community"
        title="Neighbors Forum"
        subtitle="Talk with your neighbors — recommendations, buy/sell, events, and more."
        right={<NewThreadDialog onCreated={load} />}
      />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="forum-category-tabs">
        <button onClick={() => setCategory("all")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                  category === "all"
                    ? "bg-orange-500 text-white"
                    : "bg-white/[0.03] text-zinc-300 border border-white/10 hover:bg-white/[0.06]"
                }`} data-testid="forum-cat-all">All</button>
        {Object.entries(CATEGORY_LABELS).map(([k, l]) => (
          <button key={k} onClick={() => setCategory(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                    category === k
                      ? "bg-orange-500 text-white"
                      : "bg-white/[0.03] text-zinc-300 border border-white/10 hover:bg-white/[0.06]"
                  }`} data-testid={`forum-cat-${k}`}>{l}</button>
        ))}
      </div>

      {threads.length === 0 ? (
        <Card className="p-8 text-center border-white/10" data-testid="forum-empty">
          <MessageSquare className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
          <div className="text-sm text-zinc-400">No threads yet — be the first to start a conversation.</div>
        </Card>
      ) : (
        <div className="space-y-2">
          {threads.map((t) => (
            <Card key={t.id}
                  className="p-4 border-white/10 bg-card hover:border-white/25 cursor-pointer transition"
                  onClick={() => setOpenThread(t.id)}
                  data-testid={`thread-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {t.pinned && <Badge variant="outline" className="border-fuchsia-500/40 text-fuchsia-300 text-[10px]"><Pin className="h-3 w-3 mr-0.5" /></Badge>}
                    {t.locked && <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]"><Lock className="h-3 w-3 mr-0.5" /></Badge>}
                    <Badge variant="outline" className={CATEGORY_TONE[t.category] || CATEGORY_TONE.general}>
                      {CATEGORY_LABELS[t.category] || t.category}
                    </Badge>
                  </div>
                  <div className="text-sm font-semibold text-white truncate">{t.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-1 truncate">{t.body}</div>
                  <div className="flex items-center gap-3 text-[11px] text-zinc-500 mt-2">
                    <span><User className="h-3 w-3 inline mr-1" />{t.author_name}</span>
                    <span><Clock className="h-3 w-3 inline mr-1" />{relTime(t.last_post_at || t.created_at)}</span>
                    <span>{t.reply_count || 0} repl{t.reply_count === 1 ? "y" : "ies"}</span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </ForumPageWrapper>
  );
}

function ForumPageWrapper({ children }) {
  return <div className="space-y-6">{children}</div>;
}
